import axios from 'axios';
import config from '../config/config';

const api = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: config.API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Multipart uploads need far longer than the short default timeout used for JSON
// calls -- a 50 MB document on a slow connection takes minutes, and the default
// would abort it with an ECONNABORTED that looks like an unrelated failure.
export const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Request config for multipart uploads. Pass `onProgress` to receive 0-100.
 */
export const uploadRequestConfig = (onProgress) => ({
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: UPLOAD_TIMEOUT_MS,
  ...(onProgress
    ? {
        onUploadProgress: (event) => {
          if (!event.total) return;
          onProgress(Math.round((event.loaded * 100) / event.total));
        },
      }
    : {}),
});

/**
 * Turns an upload failure into a message that names the real cause. A timeout and
 * an nginx 413 both arrive without a useful response body, so without this they
 * are indistinguishable from a generic server error.
 */
export const uploadErrorMessage = (error, fallback = 'Failed to upload document') => {
  if (error?.code === 'ECONNABORTED') {
    return 'Upload timed out. Check your connection and try again.';
  }
  if (error?.response?.status === 413) {
    return 'File rejected by the server as too large. Please contact HR.';
  }
  return error?.response?.data?.message || fallback;
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (requestConfig) => {
    const token = localStorage.getItem(config.TOKEN_STORAGE_KEY);
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(config.TOKEN_STORAGE_KEY);
      localStorage.removeItem(config.USER_STORAGE_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
